import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { join, map } from 'lodash';
import { Subject, takeUntil } from 'rxjs';

import { UiModule } from '@lockton-apps/shared/ui/lui';
import { DocumentModel } from '@lockton-apps/client-gateway/interfaces';
import { MaterialModule } from '@lockton-apps/material';
import { JoinPipe } from '@lockton-apps/shared/util/ng-lodash';

import {
  DocumentDialogData,
  DocumentDialogResultWithFolder,
  DocumentDialogResult,
  DocumentUploadRow,
} from './models';

@Component({
    selector: 'la-document-upload-dialog',
    imports: [
        CommonModule,
        UiModule,
        MaterialModule,
        JoinPipe,
        ReactiveFormsModule,
    ],
    templateUrl: './document-upload-dialog.component.html',
    styleUrls: ['./document-upload-dialog.component.scss']
})
export class DocumentUploadDialogComponent implements OnInit, OnDestroy {
  readonly acceptedFileTypes: string[];
  readonly acceptedFileTypesDescription: string;
  readonly acceptedFilesPrefix = $localize`Accepted file types`;

  files = new SelectionModel<File>(true);

  rejectedFiles!: File[];

  createNewFolderView = false;

  newFolderName: string | null = null;

  newFolderNameControl = new FormControl('', {
    validators: [Validators.required],
  });

  formArray = new FormArray([] as FormGroup[]);

  indexToRemove!: number | null;

  _destroy = new Subject<void>();

  documentRows: DocumentUploadRow[] = [];

  @Output() filesChanged = new EventEmitter<DocumentUploadRow[]>();

  constructor(
    private dialogRef: MatDialogRef<DocumentUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentDialogData
  ) {
    this.acceptedFileTypes = data.acceptedFileTypes || [];
    this.acceptedFileTypesDescription = data.acceptedFileTypesDescription || '';
  }

  ngOnInit(): void {
    this.files.compareWith = (o1, o2) => o1.name === o2.name;

    this.files.changed
      .pipe(takeUntil(this._destroy))
      .subscribe(({ added, removed }) => {
        if (added.length) {
          const newDocumentRows = added.map((file) => {
            const group = new FormGroup({
              category: new FormControl(
                this.data.defaultValues?.category || ''
              ),
              tags: new FormControl(this.data.defaultValues?.tags || []),
              visibility: new FormControl(
                this.data.defaultValues?.visibility || true
              ),
              deletionDays: new FormControl(
                this.data.defaultValues?.deletionDays || null
              ),
            });
            this.formArray.push(group);
            return {
              document: file,
              formGroup: group,
            };
          });

          this.documentRows = [...this.documentRows, ...newDocumentRows];
        }

        if (removed.length) {
          const rowsToKeep = this.documentRows.filter((row) =>
            removed.findIndex((file) => file.name === row.document.name)
          );

          this.documentRows = rowsToKeep;

          if (this.indexToRemove || this.indexToRemove === 0) {
            this.formArray.removeAt(this.indexToRemove);
          }
        }
        this.filesChanged.emit(this.documentRows);
      });

    if (this.data && this.data.files && this.data.files.length) {
      this.files.select(...this.data.files);
    }
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  onCreateNewFolderClick(): void {
    this.createNewFolderView = true;
  }

  onCancelCreateNewFolderClick(): void {
    this.createNewFolderView = false;
  }

  removeDocument(file: File, index: number): void {
    this.indexToRemove = index;
    this.files.deselect(file);
  }

  createFolder(): void {
    this.newFolderName = this.newFolderNameControl.value;
    this.createNewFolderView = false;
  }

  onFilesDropped(files: File[]): void {
    this.files.select(...Array.from(files));
  }

  onFilesRejected(files: File[]): void {
    this.rejectedFiles = files;
  }

  uploadFiles() {
    const documents: DocumentModel[] = this.files.selected.map((file, i) => ({
      file,
      isVisibleToContact: this.formArray.value[i].visibility as boolean,
      tags: join(this.formArray.value[i].tags, ', '),
      category: this.formArray.value[i].category as string,
      deletionDays: this.formArray.value[i].deletionDays || null,
      clientId: this.data.clientId,
      policyId: this.data.policyId || null,
      documentFolderId: this.data.documentFolderId,
    }));

    if (this.newFolderName) {
      this.dialogRef.close({
        folderName: this.newFolderName,
        documents: map(documents, (d) => ({ ...d, documentFolderId: null })),
      } as DocumentDialogResultWithFolder);
    } else {
      this.dialogRef.close({
        documents,
      } as DocumentDialogResult);
    }
  }
}
